import { useEffect, useRef, useState } from "react"
import { t } from "@lingui/core/macro"
import { createPortal } from "react-dom"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { colorClass } from "../../../utils/css"
import { addPageAction } from "../../../utils/newrelic"
import {
    Button,
    ButtonColor,
    ButtonSize,
    Overlay,
    Typography,
} from "../../common"
import "./V3OnboardingModal.scss"

interface V3OnboardingModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: () => void
    slides: {
        title: string
        description: string
        imgUrl: string
        imgUrlMobile: string
    }[]
}

export const V3OnboardingModal = ({
    isOpen,
    onClose,
    onSave,
    slides,
}: V3OnboardingModalProps) => {
    const [activeSlide, setActiveSlide] = useState(0)
    const backBtnRef = useRef<HTMLButtonElement>(null)
    const nextBtnRef = useRef<HTMLButtonElement>(null)
    const skipRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const tabNav = (e: KeyboardEvent) => {
            if (e.key === "Tab") {
                e.preventDefault()
                e.stopPropagation()

                if (document.activeElement === nextBtnRef.current) {
                    skipRef.current?.focus()
                } else if (
                    document.activeElement === skipRef.current &&
                    backBtnRef.current
                ) {
                    backBtnRef.current?.focus()
                } else if (
                    document.activeElement === skipRef.current &&
                    !backBtnRef.current
                ) {
                    nextBtnRef.current?.focus()
                } else {
                    nextBtnRef.current?.focus()
                }
            }
            if (e.key === "Escape") {
                onClose()
            }
        }
        document.addEventListener("keydown", tabNav, { capture: true })
        return () => {
            document.removeEventListener("keydown", tabNav, { capture: true })
        }
    }, [onClose])

    const handleNext = () => {
        if (activeSlide === slides.length - 1) {
            onSave()
            addPageAction("V3OnboardingModalClose", {
                complete: true,
            })
            return
        }
        setActiveSlide(activeSlide + 1)
    }

    const handlePrev = () => {
        if (activeSlide === 0) {
            return
        }
        setActiveSlide(activeSlide - 1)
    }

    const handleClose = () => {
        addPageAction("V3OnboardingModalClose", {
            complete: false,
            slide: activeSlide + 1,
        })
        onClose()
    }
    if (!isOpen) return null

    const currentSlide = slides[activeSlide]

    return createPortal(
        <>
            <Overlay
                className="V3OnboardingModal__overlay"
                dimmed={true}
                onClick={handleClose}
            />
            <div
                className="V3OnboardingModal__container"
                data-test="onboarding-modal"
            >
                <img
                    className="V3OnboardingModal__image"
                    data-test="onboarding-img"
                    src={slides[activeSlide].imgUrl}
                    alt={`Onboarding image ${activeSlide + 1}`}
                />
                <img
                    className="V3OnboardingModal__image_mobile"
                    data-test="onboarding-img"
                    src={slides[activeSlide].imgUrlMobile}
                    alt={`Onboarding image ${activeSlide + 1}`}
                />
                <div className="V3OnboardingModal__content">
                    <Typography
                        component="header"
                        size={Sizes.lgpx}
                        weight={Weights.bold}
                        color={colorClass.defaultTooltipColor}
                        className="V3OnboardingModal__header"
                        data-test="onboarding-title"
                    >
                        {currentSlide.title}
                    </Typography>
                    <Typography
                        component="p"
                        size={Sizes.smpx}
                        weight={Weights.normal}
                        color={colorClass.defaultTooltipColor}
                        className="V3OnboardingModal__description"
                        data-test="onboarding-description"
                    >
                        {currentSlide.description}
                    </Typography>
                    <footer className="V3OnboardingModal__footer">
                        {activeSlide > 0 && (
                            <Button
                                onClick={handlePrev}
                                className="V3OnboardingModal__btn V3OnboardingModal__btn--prev"
                                text={t`Back`}
                                color={ButtonColor.Link}
                                ref={backBtnRef}
                                data-test="back-btn"
                            />
                        )}
                        <Button
                            autoFocus
                            onClick={handleNext}
                            className="V3OnboardingModal__btn V3OnboardingModal__btn--next"
                            text={
                                activeSlide === slides.length - 1
                                    ? t`Discover`
                                    : t`Next`
                            }
                            size={ButtonSize.Medium}
                            color={ButtonColor.Blue}
                            ref={nextBtnRef}
                            data-test="next-btn"
                        />
                    </footer>
                </div>
                <Button
                    className="V3OnboardingModal__skip"
                    color={ButtonColor.Clear}
                    data-test="skip-btn"
                    onClick={onClose}
                    ref={skipRef}
                    role="button"
                    size={ButtonSize.None}
                    tabIndex={0}
                    text={t`Skip for now`}
                />
            </div>
        </>,
        document.body,
    )
}
